/**
 * Platform-accounts API (sign-up, my account, admin console). These routes
 * are new, so until `npm run gen:api` regenerates schema.d.ts they are called
 * through the untyped `request` and typed here by hand, mirroring the
 * Pydantic models in src/rai/api/routes/platform_accounts.py and admin.py.
 */
import { useQuery } from "@tanstack/react-query";

import { request } from "@/api/client";
import { API_BASE } from "@/api/config";
import { errorFrom } from "@/api/errors";
import { getAccessToken, hasRefreshToken, refreshAccess } from "@/api/session";
import { formatINR } from "@/lib/format";

import type { ProOfferChip } from "../pro-offer/lib";

export type PlatformRole = "admin" | "ra" | "investor" | "employee";
export type VerificationStatus =
  "verified" | "pending" | "mismatch" | "expired";
export const CHANNELS = ["in_app", "push", "email", "sms", "whatsapp"] as const;
export type Channel = (typeof CHANNELS)[number];
export type ChannelSettings = Record<
  Channel,
  { enabled: boolean; paid: boolean }
>;

export interface Plan {
  code: string;
  role: "ra" | "investor";
  name: string;
  description: string | null;
  price_monthly: number;
  currency: string;
  client_limit: number | null;
  features: Record<string, unknown>;
  is_default: boolean;
  is_placeholder: boolean;
  active: boolean;
  /** Listed on the pricing and sign-up pages (V052); RA plans are hidden. */
  public?: boolean;
  sort_order: number;
  /** V064: the launch offer (e.g. 50% off for the first 6 months); null = none. */
  promo_price?: number | null;
  promo_label?: string | null;
  promo_months?: number | null;
  promo_until?: string | null;
}

/** A plan's launch offer when it is on (V064): the offer price, regular price, months. */
export interface PlanOffer {
  price: number;
  regular: number;
  label: string;
  months: number;
}

export function planOffer(
  plan: Pick<Plan, "price_monthly" | "promo_price" | "promo_label" | "promo_months" | "promo_until">,
  today: string = new Date().toISOString().slice(0, 10),
): PlanOffer | null {
  if (!plan.promo_price || !plan.promo_months || plan.promo_price >= plan.price_monthly) return null;
  if (plan.promo_until && plan.promo_until < today) return null;
  return {
    price: plan.promo_price,
    regular: plan.price_monthly,
    label: plan.promo_label || `Offer for the first ${plan.promo_months} months`,
    months: plan.promo_months,
  };
}

export interface PlansResponse {
  plans: Plan[];
  channels: ChannelSettings;
  signup: { investor_open?: boolean; ra_open?: boolean; trial_days?: number };
}

export interface Verification {
  status: VerificationStatus;
  reason: string;
  checks: Record<string, unknown>;
  register_entry: Record<string, unknown> | null;
}

export interface RAProfile {
  user_id: number;
  sebi_reg_no: string;
  registered_name: string;
  display_name: string;
  bio: string | null;
  specialisations: string[];
  segments: string[];
  languages: string[];
  city: string | null;
  state: string | null;
  pincode: string | null;
  verification_status: VerificationStatus;
  verified_at: string | null;
  /** V063: the branded public profile (shown on RA Growth and RA Pro). */
  headline?: string | null;
  website?: string | null;
  accent?: string | null;
  /** V066: the admin-approved photo's key; null = the initials. */
  photo?: string | null;
}

export interface MyRAProfile extends RAProfile {
  verification_detail: {
    reason?: string;
    checks?: Record<string, unknown>;
    register?: Record<string, unknown> | null;
    /** V063: a member asked to become an RA; the role changes once the code is in. */
    upgrade?: { plan_code: string; requested_at?: string };
  };
  verified_by: string | null;
  has_certificate: boolean;
  certificate_mime: string | null;
  certificate_size: number | null;
  certificate_uploaded_at: string | null;
}

export interface Subscription {
  plan_code: string;
  plan_name: string;
  status: "trial" | "active" | "cancelled";
  period_start: string;
  period_end: string | null;
  price_monthly: number;
  client_limit: number | null;
  features: Record<string, unknown>;
  is_placeholder: boolean;
  /** A paid plan the user picked: active now, free until payments are live. */
  payment_pending?: boolean;
  note?: string | null;
  /** V065: an admin's complimentary period (no charge until period_end). */
  complimentary?: boolean;
  /** What applies after it: the regular price or the plan's launch offer. */
  after_price?: "regular" | "offer" | null;
  /** What the user pays a month once this period ends (GST included). */
  next_price?: number | null;
}

/** What happened to an email the API tried to send. */
export interface EmailSend {
  status: string;
  to?: string | null;
  message?: string | null;
}

export interface Account {
  id: number;
  email: string;
  /** Confirmed with the emailed code (until then: a banner, and invitations wait). */
  email_verified: boolean;
  role: PlatformRole;
  stored_role: string;
  display_name: string | null;
  created_at: string;
  ra_profile: MyRAProfile | null;
  investor_profile: {
    city: string | null;
    state: string | null;
    pincode: string | null;
    experience: string | null;
    interests: string[];
  } | null;
  subscription: Subscription | null;
  /** Paid plans only: false = the app shows only Account and Payment. */
  access: Access;
  phone?: string | null;
  /** V063: an RA's client: free (the RA's plan covers them) or subscribed (own membership too). */
  client_of?: ClientOf | null;
}

export interface ClientOf {
  ra_user_id: number;
  ra_name: string | null;
  sebi_reg_no: string | null;
  membership: "free" | "subscribed";
}

/** POST /platform/ra/lookup: SEBI's register entry for an INH number (V063). */
export interface RegLookup {
  reg_no: string;
  found: boolean;
  valid: boolean;
  name: string | null;
  trade_name: string | null;
  city: string | null;
  state: string | null;
  validity_to: string | null;
  perpetual: boolean | null;
  /** Masked: where the one-time code will go. */
  email_to: string | null;
  has_email: boolean;
  /** Already verified on another account. */
  claimed: boolean;
  /** RA sign-up is open. */
  open: boolean;
}

/** What an RA plan includes, as short lines (from its features; V063). */
export function raPlanPerks(plan: Pick<Plan, "client_limit" | "features">): string[] {
  const f = plan.features ?? {};
  const out: string[] = [];
  if (plan.client_limit) out.push(`Up to ${plan.client_limit.toLocaleString("en-IN")} clients, free for them`);
  if (f.research_tools !== false) out.push("Every research tool");
  if (f.calls !== false) out.push("Publish calls with the SEBI disclosures added");
  out.push("Track record and client invitations");
  out.push("Delivery in the app, by email and on WhatsApp");
  if (f.featured) out.push("Featured directory listing");
  if (f.search_priority) out.push("Search priority");
  if (f.branded_profile) out.push("Branded public profile page");
  if (f.team_logins) out.push("Team logins (coming soon)");
  if (f.priority_support) out.push("Priority support");
  return out;
}

/** Title Case for a register name in capitals: "RAVI KUMAR SHARMA" -> "Ravi Kumar Sharma". */
export function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase());
}

export interface Access {
  active: boolean;
  /** admin | active | trial | no_plan | plan_retired | payment_pending | expired | trial_ended */
  reason: string;
  /** Last day of the trial or paid period (inclusive). */
  until: string | null;
  kind?: "admin" | "trial" | "paid" | "pending" | "none" | "ra_client" | "complimentary";
  /** Days left in the period, from the database (IST). */
  days_left?: number | null;
  plan_code?: string | null;
  plan_name?: string | null;
  /** V035: "client" = an RA's client (their RA's calls and the basic market pages). */
  tier?: "full" | "client";
  ra_user_id?: number | null;
  ra_name?: string | null;
  /** When the RA's service stopped; the account is inactive after `grace_until`. */
  service_ended_at?: string | null;
  grace_until?: string | null;
}

/** A manual payment (V032). Prices include GST: total = taxable value + GST. */
export interface Payment {
  id: number;
  user_id: number;
  email: string;
  display_name: string | null;
  platform_role: PlatformRole | null;
  plan_code: string;
  plan_name: string;
  /** What the user says they paid. */
  amount: number;
  base_amount: number;
  gst_rate: number;
  gst_amount: number;
  /** What was due. */
  total_amount: number;
  currency: string;
  method: string;
  reference: string | null;
  paid_on: string;
  months: number;
  status: "submitted" | "approved" | "rejected";
  submitted_at: string;
  user_note: string | null;
  reviewed_at: string | null;
  reviewed_by_email: string | null;
  review_note: string | null;
  period_start: string | null;
  period_end: string | null;
  /** The user now (V034): can't sign in until the address is confirmed. */
  email_verified?: boolean;
  plan_kind?: string | null;
  plan_period_end?: string | null;
  /** V064: the regular price a month, what was charged a month, and the offer applied. */
  list_price?: number | null;
  unit_price?: number | null;
  promo_label?: string | null;
  /** V047: voided as a test payment (kept, counted nowhere). */
  voided_at?: string | null;
  voided_by_email?: string | null;
  void_reason?: string | null;
  /** V068: paid online through PayU (method "payu"; reference = our transaction id). */
  gateway_txnid?: string | null;
  mihpayid?: string | null;
  pay_mode?: string | null;
  card_masked?: string | null;
  refunded_amount?: number;
  refunds?: Refund[];
}

/** A PayU refund (V068): queued with PayU, then refunded; failed = nothing refunded. */
export interface Refund {
  id: number;
  amount: number;
  status: "requested" | "queued" | "success" | "failed";
  reason: string;
  requested_at: string;
  message: string | null;
  end_access: boolean;
  access_ended_until: string | null;
}

/** PayU's payment modes in words. */
export const PAY_MODE_LABEL: Record<string, string> = {
  UPI: "UPI",
  CC: "Credit card",
  DC: "Debit card",
  NB: "Net banking",
  CASH: "Wallet",
  EMI: "EMI",
  BNPL: "Pay later",
};

/** Bank-transfer details (Admin → Settings → Payment details). Empty UPI ID = bank
 * transfer only. Customers get them all empty while bank transfer is switched off. */
export interface PayTo {
  upi_id: string;
  payee_name: string;
  account_name: string;
  account_number: string;
  ifsc: string;
  bank_name: string;
  branch: string;
  account_type: string;
  instructions: string;
}

export const PAY_TO_FIELDS: { key: keyof PayTo; label: string; hint?: string }[] = [
  { key: "account_name", label: "Account name" },
  { key: "account_number", label: "Account number" },
  { key: "ifsc", label: "IFSC", hint: "e.g. ABCD0123456" },
  { key: "bank_name", label: "Bank name" },
  { key: "branch", label: "Branch" },
  { key: "account_type", label: "Account type", hint: "Current or Savings" },
  { key: "upi_id", label: "UPI ID (optional)", hint: "Leave empty for bank transfer only" },
  { key: "payee_name", label: "UPI payee name (optional)" },
];

export interface PlanDue {
  code: string;
  name: string;
  role: string;
  price_monthly: number;
  base_amount: number;
  gst_rate: number;
  gst_amount: number;
  total_amount: number;
  /** V064: the regular price a month, and the launch offer when it applies. */
  regular_price?: number | null;
  promo_label?: string | null;
  promo_months_left?: number;
}

export interface PaymentPage {
  pay_to: PayTo;
  /** Admin → Settings → Payment details → "Bank transfer" (off by default since 29 Oct 2026). */
  bank_transfer?: boolean;
  /** V068: PayU online payment, when an admin has switched it on. */
  online?: { enabled: boolean; phone_needed: boolean };
  plan: PlanDue | null;
  plans: PlanDue[];
  access: Access;
  payments: Payment[];
}

/** Admin: one user with plan and access. */
export interface AdminUserDetail {
  id: number;
  email: string;
  role: string;
  platform_role: PlatformRole | null;
  display_name: string | null;
  phone: string | null;
  disabled: boolean;
  created_at: string;
  last_login_at: string | null;
  ai_calls_access: boolean;
  email_verified: boolean;
  sebi_reg_no: string | null;
  verification_status: VerificationStatus | null;
  plan_code: string | null;
  plan_name: string | null;
  period_end: string | null;
  payment_pending: boolean;
  /** V065: an admin's complimentary period, what applies after it, and that price a month. */
  complimentary?: boolean;
  after_price?: "regular" | "offer" | null;
  next_price?: number | null;
  access: Access;
  email_status: string | null;
}

export const PAYMENT_STATUS_LABEL: Record<Payment["status"], string> = {
  submitted: "Waiting for review",
  approved: "Approved",
  rejected: "Rejected",
};

export interface AdminUserRow {
  id: number;
  email: string;
  role: string;
  platform_role: PlatformRole | null;
  display_name: string | null;
  disabled: boolean;
  created_at: string;
  last_login_at: string | null;
  sebi_reg_no: string | null;
  verification_status: VerificationStatus | null;
  subscription: string | null;
  /** Ticked by an admin: this user sees the platform's AI calls feed. */
  ai_calls_access: boolean;
  email_verified?: boolean;
  phone?: string | null;
  plan_code?: string | null;
  plan_name?: string | null;
  plan_active?: boolean;
  /** V033 subscriptions.kind: trial | pending | paid | ended. */
  plan_kind?: string | null;
  period_end?: string | null;
  /** V065: an admin's complimentary period, and what applies after it. */
  complimentary?: boolean;
  after_price?: "regular" | "offer" | null;
  /** V035: an RA's client: the RA, ra_client | ra_client_grace | ra_client_ended. */
  client_of?: string | null;
  client_status?: string | null;
  client_inactive_on?: string | null;
  /** An active client of this RA, even with a plan of their own. */
  ra_client_of?: string | null;
  /** V063: "free" (the RA's plan covers them) or "subscribed" (own membership too). */
  client_membership?: "free" | "subscribed" | null;
  /** V035: an RA's client places used (active + invitations pending 30 days) / limit. */
  clients_used?: number | null;
  client_limit?: number | null;
  /** V038: a free trial that starts when they confirm their email. */
  trial_waiting?: boolean;
  /** V043: set on a deleted (erased) account; the row is read-only. */
  deleted_at?: string | null;
  /** V083: how AI paper trades reach this person, set by admins. */
  ai_channels?: AiChannels;
  /** They agreed to WhatsApp: without it nothing is sent to them, whatever the chip says. */
  whatsapp_agreed?: boolean;
  /** What the senders do now: off while a ticked user's plan isn't active. */
  ai_calls_effective?: boolean;
  /** "plan_inactive": ticked, but off until they renew (the tick is kept). */
  ai_calls_reason?: string | null;
  /** V087: the newest Pro agreement offer (Admin → Users "Pro agreement"). */
  pro_offer?: ProOfferChip | null;
  /** V103: their app installs and browsers, most recently seen first. */
  apps?: AppDevice[];
}

/** V103: one install's version (rai.platform.app_devices). */
export interface AppDevice {
  platform: "android" | "ios" | "web";
  app_version: string | null;
  build: number | null;
  screens: string | null;
  screens_short: string | null;
  pending: string | null;
  /** "Android · 1.0.4 (build 4) · screens 02 Nov 18:20" */
  label: string;
  last_seen_at: string;
}

/** V083: Admin -> Users, next to "AI & calls" (push is stored; nothing sends it yet). */
export interface AiChannels {
  in_app: boolean;
  email: boolean;
  whatsapp: boolean;
  push: boolean;
}

/** GET /admin/email: is email set up, and what the provider said last. */
export interface EmailStatus {
  configured: boolean;
  provider: string | null;
  sender: string;
  reply_to: string | null;
  domain_unverified: boolean;
  domain_unverified_at: string | null;
  warning: string | null;
  last: {
    at: string;
    kind: string;
    status: string;
    error_kind: string | null;
    error: string | null;
  } | null;
  sent_7d: number;
  failed_7d: number;
  skipped_7d: number;
}

/** A short, human line for an email outcome. */
export function emailOutcome(e: EmailSend | null | undefined): string | null {
  if (!e) return null;
  switch (e.status) {
    case "sent":
      return `We emailed a code to ${e.to ?? "you"}.`;
    case "sent_earlier":
      return `A code was already sent to ${e.to ?? "you"}; it's valid for 15 minutes.`;
    case "already_confirmed":
      return "Your email is already confirmed.";
    case "not_configured":
      return "Email isn't set up on this server yet, so no code was sent.";
    case "rate_limited":
      return e.message ?? "A code was sent a moment ago; wait a minute.";
    case "domain_unverified":
      return "The email provider refused this address (the sender's domain isn't verified yet). An admin has been told.";
    case "no_register_email":
      return "SEBI's register has no email for this registration, so an admin will review it.";
    default:
      return e.message ?? "The email couldn't be sent. Try again later.";
  }
}

export interface QueueItem {
  id: number;
  kind: string;
  subject_user_id: number;
  reason: string;
  detail: {
    reason?: string;
    checks?: Record<string, unknown>;
    register?: Record<string, unknown> | null;
  };
  status: string;
  created_at: string;
  resolved_at: string | null;
  resolution_note: string | null;
  email: string;
  display_name: string | null;
  disabled: boolean;
  sebi_reg_no: string | null;
  registered_name: string | null;
  verification_status: VerificationStatus | null;
  certificate_mime: string | null;
  certificate_size: number | null;
  certificate_sha256: string | null;
  has_certificate: boolean;
  /** The city the RA gave at sign-up. */
  city?: string | null;
  certificate_uploaded_at?: string | null;
}

export interface Metrics {
  // employees (V036) appear only once there are any
  users_by_role: Record<Exclude<PlatformRole, "employee">, number> & { employee?: number };
  users_total: number;
  new_users_7d: number;
  disabled_users: number;
  ras_by_verification: Record<VerificationStatus, number>;
  open_review_cases: number;
  live_subscriptions: { plan_code: string; status: string; n: number }[];
  calls: {
    date: string;
    ai_calls_today: number | null;
    ra_calls_today: number | null;
  };
  channels_enabled: string[];
}

export function get<T>(
  path: string,
  query?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  return request<T>("get", path, { query, signal });
}

export function send<T>(
  method: "post" | "put" | "patch",
  path: string,
  body: unknown,
): Promise<T> {
  return request<T>(method, path, { body });
}

/** A GET with TanStack Query; the key matches `useApi`, so invalidation works the same way. */
export function usePlatform<T>(
  path: string,
  query?: Record<string, unknown>,
  enabled = true,
) {
  return useQuery({
    queryKey: [path, null, query ?? null],
    queryFn: ({ signal }) => get<T>(path, query, signal),
    enabled,
  });
}

/** A protected file (certificate) as a Blob, with the bearer token. */
export async function fetchBlob(path: string): Promise<Blob> {
  if (!getAccessToken() && hasRefreshToken()) await refreshAccess();
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw await errorFrom(res);
  return res.blob();
}

export const MAX_CERT_BYTES = 5 * 1024 * 1024;
export const CERT_TYPES = ["application/pdf", "image/png", "image/jpeg"];

/** Read a file as a data: URL (the API accepts base64 or a data URL). */
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(reader.error ?? new Error("could not read the file"));
    reader.readAsDataURL(file);
  });
}

/** An optional certificate: fine when there is none. */
export function checkOptionalCertificate(file: File | null): string | null {
  return file ? checkCertificate(file) : null;
}

export function checkCertificate(file: File | null): string | null {
  if (!file) return "Upload your SEBI registration certificate.";
  if (!CERT_TYPES.includes(file.type))
    return "The certificate must be a PDF, PNG or JPEG.";
  if (file.size > MAX_CERT_BYTES)
    return "The certificate must be 5 MB or smaller.";
  return null;
}

export function splitTags(s: string): string[] {
  return s
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

export const ROLE_LABEL: Record<PlatformRole, string> = {
  admin: "Admin",
  ra: "Research Analyst",
  investor: "Investor",
  employee: "Employee",
};

export function priceLabel(
  plan: Pick<Plan, "price_monthly" | "is_placeholder">,
): string {
  if (plan.price_monthly === 0) return "Free";
  const p = `${formatINR(plan.price_monthly, 0)}/month incl. GST`;
  return plan.is_placeholder ? `${p} (placeholder)` : p;
}
